import {type ReactNode, useRef, Suspense, useMemo} from 'react';
import {Disclosure, Listbox} from '@headlessui/react';
import {defer, type LoaderArgs} from '@shopify/remix-oxygen';
import {
  useLoaderData,
  Await,
  useSearchParams,
  useLocation,
  useTransition,
} from '@remix-run/react';
import React from 'react';
import {
  AnalyticsPageType,
  Money,
  ShopifyAnalyticsProduct,
  ShopPayButton,
} from '@shopify/hydrogen';
import {
  Heading,
  IconCaret,
  IconCheck,
  IconClose,
  ProductGallery,
  ProductSwimlane,
  Section,
  Skeleton,
  Text,
  Link,
  AddToCartButton,
  Button,
} from '~/components';
import {getExcerpt} from '~/lib/utils';
import {seoPayload} from '~/lib/seo.server';
import invariant from 'tiny-invariant';
import clsx from 'clsx';
import type {
  ProductVariant,
  SelectedOptionInput,
  Product as ProductType,
  Shop,
  ProductConnection,
  CollectionConnection,
} from '@shopify/hydrogen/storefront-api-types';
import {MEDIA_FRAGMENT, PRODUCT_CARD_FRAGMENT} from '~/data/fragments';
import type {Storefront} from '~/lib/type';
import type {Product} from 'schema-dts';
import {routeHeaders, CACHE_SHORT} from '~/data/cache';
import type {LinksFunction} from '@shopify/remix-oxygen';

//components
import ProductDetails from 'components/productDetails';
import ProductDetailsRecommendations from 'components/productDetails/ProductDetailsRecommendations';
import GoogleReviews from 'components/modules/GoogleReviews';
import FacebookReviews from 'components/modules/FacebookReviews';
import LooxReviews from 'components/modules/LooxReviews';
import Container from 'components/Container';

//domains
import {
  useCollectionQuery,
  getCollectionsForBreadcrumb,
} from 'domains/collections';
import {SanityProductQueryResponse, useProductQuery} from 'domains/product';
import {getLangagueCodeForSanityQuery, isTWSite} from '~/domains/sanity';
import {useIsHydrated} from '~/hooks/useIsHydrated';
import {getSession} from '~/cookie.server';
//translation
import translation from 'translations/global.json';
import {routeLocale} from 'domains/global';

const {LRUCache} = require('lru-cache');
const cache = new LRUCache({
  max: 500,
  ttl: 1000 * 60 * 60 * 24,
});

export const headers = routeHeaders;

let scripts: any = () => {
  return [
    {
      src: 'https://preproduct.onrender.com/preproduct-embed.js',
      crossOrigin: 'anonymous',
      async: 'true',
    },
  ];
};

export let handle = {scripts};

export const links: LinksFunction = () => {
  return [
    {
      rel: 'stylesheet',
      href: 'https://cdn.jsdelivr.net/npm/swiper@8/swiper-bundle.min.css',
    },
  ];
};

export async function loader({params, request, context}: LoaderArgs) {
  const {language, country} = context.storefront.i18n;
  const session = await getSession(request.headers.get('Cookie'));
  const previous_lang = await session.get('previous-lang');
  const current_lang = params.lang ?? 'zh_tw';
  const is_lang_changed = previous_lang !== current_lang;

  const {productHandle} = params;
  invariant(productHandle, 'Missing productHandle param, check route filename');

  const searchParams = new URL(request.url).searchParams;

  const selectedOptions: SelectedOptionInput[] = [];
  searchParams.forEach((value, name) => {
    selectedOptions.push({name, value});
  });

  const {shop, product} = await context.storefront.query<{
    product: ProductType & {selectedVariant?: ProductVariant};
    shop: Shop;
  }>(PRODUCT_QUERY, {
    variables: {
      handle: productHandle,
      selectedOptions,
      country: isTWSite(request.url) ? 'TW' : 'HK',
      language: context.storefront.i18n.language,
    },
  });

  if (!product?.id) {
    throw new Response(null, {status: 404});
  }

  const recommended = getRecommendedProducts(context.storefront, product.id);
  const firstVariant = product.variants.nodes[0];
  const selectedVariant = product.selectedVariant ?? firstVariant;

  const productAnalytics: ShopifyAnalyticsProduct = {
    productGid: product.id,
    variantGid: selectedVariant.id,
    name: product.title,
    variantName: selectedVariant.title,
    brand: product.vendor,
    price: selectedVariant.price.amount,
  };

  let sanityProductQueryResponse: SanityProductQueryResponse;

  if (cache.has('sanityProductQueryResponse') && !is_lang_changed) {
    sanityProductQueryResponse = cache.get('sanityProductQueryResponse');
  } else {
    sanityProductQueryResponse = await useProductQuery({
      languageCode: getLangagueCodeForSanityQuery(language),
      vendor: product.vendor,
      handle: product.handle,
    });
    await cache.set('sanityProductQueryResponse', sanityProductQueryResponse);
  }

  const collectionIdsForBreadcrumb = getCollectionsForBreadcrumb({
    collections: product.collections.nodes,
    collectionStructure:
      sanityProductQueryResponse.productSettings.collection_structure,
  });
  let collectionsForBreadcrumb;

  if (collectionIdsForBreadcrumb?.map((c) => c.id)) {
    try {
      if (language === 'EN') {
        const {collections} = await context.storefront.query<{
          collections: CollectionConnection['nodes'];
        }>(
          `#graphql
        query breadcrumbCollectionsInEnglish($gids: [ID!]!)
        {
          collections: nodes(ids: $gids) {
            ...on Collection{
              id
              title
              handle
            }
          }
        }
      `,
          {
            variables: {
              gids: collectionIdsForBreadcrumb?.map((c) => c.id) ?? [''],
            },
          },
        );
        collectionsForBreadcrumb = collections;
      } else {
        const {collections} = await context.storefront.query<{
          collections: CollectionConnection['nodes'];
        }>(
          `#graphql
        query breadcrumbCollectionsInCantonese($country: CountryCode, $language: LanguageCode, $gids: [ID!]!)
        @inContext(country: $country, language: $language) {
          collections: nodes(ids: $gids) {
            ...on Collection{
              id
              title
              handle
            }
          }
        }
      `,
          {
            variables: {
              country,
              language,
              gids: collectionIdsForBreadcrumb?.map((c) => c.id) ?? [''],
            },
          },
        );
        collectionsForBreadcrumb = collections;
      }
    } catch (e) {
      console.log(e);
    }
  }

  const seo = seoPayload.product({
    product,
    selectedVariant,
    url: request.url,
  });

  const selectedLocale =
    context.storefront.i18n.language.toLowerCase() as routeLocale;

  return defer(
    {
      product,
      shop,
      storeDomain: shop.primaryDomain.url,
      recommended,
      analytics: {
        pageType: AnalyticsPageType.product,
        resourceId: product.id,
        products: [productAnalytics],
        totalValue: parseFloat(selectedVariant.price.amount),
      },
      translation: translation[selectedLocale],
      collectionsForBreadcrumb,
      seo,
      productSettings: sanityProductQueryResponse.productSettings,
      sizeGuide: sanityProductQueryResponse.sizeGuide,
      bundles: sanityProductQueryResponse.sanity_product.bundles,
    },
    {
      headers: {
        'Cache-Control': CACHE_SHORT,
      },
    },
  );
}

export default function ProductDetailsPage() {
  const {product, shop, recommended, translation} =
    useLoaderData<typeof loader>();
  // const {media, title, vendor, descriptionHtml} = product;
  // const {shippingPolicy, refundPolicy} = shop;
  const isHydrated = useIsHydrated();

  return (
    <>
      {isHydrated && <ProductDetails />}
      <Suspense fallback={<Skeleton className="h-32" />}>
        <Await
          errorElement="There was a problem loading related products"
          resolve={recommended}
        >
          {(products) => (
            <ProductDetailsRecommendations
              products={products.filter((p, i) => i !== 0)}
            />
          )}
        </Await>
        <section className="">
          <header className="text-center mb-4">
            <h2 className="text-xl font-bold uppercase tracking-wide">
              {translation.customer_reviews_title ?? 'Customer Reviews'}
            </h2>
          </header>
          <Container>
            <LooxReviews
              module={{
                _type: 'module.looxReviews',
                _key: 'product-page-loox-reviews',
                active: true,
              }}
            />
            <GoogleReviews />
            <FacebookReviews />
          </Container>
        </section>
      </Suspense>
    </>
  );
}

const PRODUCT_VARIANT_FRAGMENT = `#graphql
  fragment ProductVariantFragment on ProductVariant {
    id
    availableForSale
    quantityAvailable
    selectedOptions {
      name
      value
    }
    image {
      id
      url
      altText
      width
      height
    }
    price {
      amount
      currencyCode
    }
    compareAtPrice {
      amount
      currencyCode
    }
    sku
    title
    unitPrice {
      amount
      currencyCode
    }
    product {
      title
      handle
    }
  }
`;

const PRODUCT_QUERY = `#graphql
  ${MEDIA_FRAGMENT}
  ${PRODUCT_VARIANT_FRAGMENT}
  query Product(
    $country: CountryCode
    $language: LanguageCode
    $handle: String!
    $selectedOptions: [SelectedOptionInput!]!
  ) @inContext(country: $country, language: $language) {
    product(handle: $handle) {
      id
      tags
      title
      vendor
      handle
      descriptionHtml
      description
      options {
        name
        values
      }
      productType
      collections(first: 30) {
        nodes {
          title
          id
          shipping_status: metafield(
            namespace: "collection_metadata"
            key: "shipping_status"
          ) {
            value
            type
          }
          is_brand: metafield(
            namespace: "collection_metadata"
            key: "is_brand"
          ) {
            value
            type
          }
          size_chart: metafield(
            namespace: "collection_metadata"
            key: "size_chart"
          ) {
            value
            type
          }
        }
      }
      selectedVariant: variantBySelectedOptions(selectedOptions: $selectedOptions) {
        ...ProductVariantFragment
      }
      media(first: 20) {
        nodes {
          ...Media
        }
      }
      variants(first: 100) {
        nodes {
          ...ProductVariantFragment
        }
      }
      seo {
        description
        title
      }
    }
    shop {
      name
      primaryDomain {
        url
      }
      shippingPolicy {
        body
        handle
      }
      refundPolicy {
        body
        handle
      }
    }
  }
`;

const RECOMMENDED_PRODUCTS_QUERY = `#graphql
  query productRecommendations(
    $productId: ID!
    $count: Int
    $country: CountryCode
    $language: LanguageCode
  ) @inContext(country: $country, language: $language) {
    recommended: productRecommendations(productId: $productId) {
      id
      title
      vendor
      handle
      options {
        name
        values
      }
      tags
      media(first: 20) {
        nodes {
          __typename
          mediaContentType
          alt
          previewImage {
            url
          }
          ... on MediaImage {
            id
            image {
              url
              width
              height
            }
          }
        }
      }
      variants(first: 30) {
        nodes {
          id
          availableForSale
          quantityAvailable
          selectedOptions {
            name
            value
          }
          image {
            id
            url
            altText
            width
            height
          }
          price {
            amount
            currencyCode
          }
          compareAtPrice {
            amount
            currencyCode
          }
          sku
          title
          unitPrice {
            amount
            currencyCode
          }
        }
      }
    }
    additional: products(first: $count, sortKey: BEST_SELLING) {
      nodes {
        id
      title
      vendor
      handle
      options {
        name
        values
      }
      tags
      media(first: 20) {
        nodes {
          __typename
          mediaContentType
          alt
          previewImage {
            url
          }
          ... on MediaImage {
            id
            image {
              url
              width
              height
            }
          }
        }
      }
      variants(first: 100) {
        nodes {
          id
          availableForSale
          quantityAvailable
          selectedOptions {
            name
            value
          }
          image {
            id
            url
            altText
            width
            height
          }
          price {
            amount
            currencyCode
          }
          compareAtPrice {
            amount
            currencyCode
          }
          sku
          title
          unitPrice {
            amount
            currencyCode
          }
        }
      }
      }
    }
  }
`;

async function getRecommendedProducts(
  storefront: Storefront,
  productId: string,
) {
  const products = await storefront.query<{
    recommended: ProductType[];
    additional: ProductConnection;
  }>(RECOMMENDED_PRODUCTS_QUERY, {
    variables: {productId, count: 12},
  });

  invariant(products, 'No data returned from Shopify API');

  const mergedProducts = products.recommended
    .concat(products.additional.nodes)
    .filter(
      (value, index, array) =>
        array.findIndex((value2) => value2.id === value.id) === index,
    );

  const originalProduct = mergedProducts
    .map((item: ProductType) => item.id)
    .indexOf(productId);

  mergedProducts.splice(originalProduct, 1);

  return mergedProducts;
}
